import { GridFilterItem } from '@mui/x-data-grid'
import { useEffect, useState, useCallback, SyntheticEvent } from 'react'

export type AutocompleteFilterValue = string | string[] | null

export interface AutocompleteFilterState {
  selectedValue: AutocompleteFilterValue
  inputValue: string
  isMultiSelect: boolean
  safeValue: AutocompleteFilterValue
}

export interface AutocompleteFilterHandlers {
  handleInputChange: (_: SyntheticEvent, newInputValue: string) => void
  handleChange: (_: SyntheticEvent, newValue: AutocompleteFilterValue) => void
}

/**
 * Determines if the filter is in multi-select mode based on the operator
 */
export const isMultiSelectFilter = (operator: string): boolean => {
  return operator === 'isAnyOf'
}

/**
 * Calculates the initial value for the filter based on the operator type
 */
export const getInitialFilterValue = ({
  value,
  isMultiSelect,
}: {
  value: AutocompleteFilterValue | undefined
  isMultiSelect: boolean
}): AutocompleteFilterValue => {
  if (isMultiSelect) {
    if (Array.isArray(value)) {
      return value
    }
    if (value && typeof value === 'string') {
      return [value]
    }
    return []
  }
  if (Array.isArray(value)) {
    return value[0] || null
  }
  return value ?? null
}

/**
 * Ensures the value matches the expected type for the current mode
 */
export const getSafeFilterValue = ({
  selectedValue,
  isMultiSelect,
}: {
  selectedValue: AutocompleteFilterValue
  isMultiSelect: boolean
}): AutocompleteFilterValue => {
  if (isMultiSelect) {
    return Array.isArray(selectedValue) ? selectedValue : []
  }
  return Array.isArray(selectedValue) ? selectedValue[0] || null : selectedValue
}

/**
 * Custom hook for managing autocomplete filter state and handlers
 */
export const useAutocompleteFilter = ({
  item,
  applyValue,
}: {
  item: GridFilterItem
  applyValue: (item: GridFilterItem) => void
}): AutocompleteFilterState & AutocompleteFilterHandlers => {
  const isMultiSelect = isMultiSelectFilter(item.operator)

  const [selectedValue, setSelectedValue] = useState<AutocompleteFilterValue>(() =>
    getInitialFilterValue({ value: item.value, isMultiSelect })
  )
  const [inputValue, setInputValue] = useState('')

  // Update selectedValue when operator changes
  useEffect(() => {
    const newValue = getInitialFilterValue({ value: item.value, isMultiSelect })
    setSelectedValue(newValue)
  }, [item.operator, item.value, isMultiSelect])

  const handleInputChange = useCallback((_: SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue)
  }, [])

  const handleChange = useCallback(
    (_: SyntheticEvent, newValue: AutocompleteFilterValue) => {
      setSelectedValue(newValue)

      // Use undefined for null/empty values to signal filter removal
      if (newValue === null || newValue === undefined || (Array.isArray(newValue) && newValue.length === 0)) {
        applyValue({ ...item, value: undefined })
      } else {
        applyValue({ ...item, value: newValue })
      }
    },
    [item, applyValue]
  )

  const safeValue = getSafeFilterValue({ selectedValue, isMultiSelect })

  return {
    selectedValue,
    inputValue,
    isMultiSelect,
    safeValue,
    handleInputChange,
    handleChange,
  }
}
